import React, { useCallback, useState } from 'react';
import { customerExperiencesNavigationResource } from '@jira/router-resources__customer-experiences-navigation/src/customerExperiencesNavigationResource.tsx';
import type { PreloadedQuery } from 'react-relay';
import { MENU_ID_CUSTOMER_SERVICE } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import {
	L1_MENU_ID,
	type Nav4DragAndDropMenuItemProps,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { Nav4ExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { useConfigurableMessageAndIconMap } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import { getTestId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import {
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import { useResource } from '@atlassian/react-resource-router';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { CustomerExperiencesExpandableMenu } from './customer-experiences-expandable-menu/CustomerExperiencesExpandableMenu.tsx';
import type { CustomerExperiencesListQuery } from './customer-experiences-expandable-menu/__generated__/CustomerExperiencesListQuery.graphql';
import { CreateExperienceButton } from './CreateExperienceButton.tsx';

interface Props extends Nav4DragAndDropMenuItemProps {
	isExpanded?: boolean;
}

export const CustomerExperiencesMenuItem = (props: Props): React.JSX.Element => {
	const {
		draggableRef,
		dropIndicator,
		hasDragIndicator,
		isDragging,
		visualContentRef,
		onExpandFlyoutToggle,
	} = props;
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.CUSTOMER_SERVICE);
	const ref = draggableRef;
	const [hasLoadedContent, setHasLoadedContent] = useState(false);
	const isAdmin = useIsAdmin();

	const {
		data: queryReference,
		loading,
		error,
		refresh,
	} = useResource<PreloadedQuery<CustomerExperiencesListQuery>>(
		customerExperiencesNavigationResource,
	);

	const handleSystemToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				setHasLoadedContent(true);

				if (!queryReference && !loading && !error) {
					refresh();
				}
			}
		},
		[queryReference, loading, error, refresh],
	);

	const handleExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				setHasLoadedContent(true);

				if (!queryReference && !loading && !error) {
					refresh();
				}
			}
			onExpandFlyoutToggle?.(hasExpanded);
		},
		[onExpandFlyoutToggle, queryReference, loading, error, refresh],
	);

	return (
		<Nav4ExpandableMenuItem
			dropTargetRef={visualContentRef}
			dropIndicator={dropIndicator}
			isDragging={isDragging}
			menuId={MENU_ID_CUSTOMER_SERVICE}
			onSystemToggle={handleSystemToggle}
			onExpansionToggle={handleExpansionToggle}
		>
			<ExpandableMenuItemTrigger
				actionsOnHover={isAdmin ? <CreateExperienceButton /> : undefined}
				aria-label={formattedMessage}
				elemBefore={icon}
				hasDragIndicator={hasDragIndicator}
				interactionName="nav4-sidebar-customer-service"
				isDragging={isDragging}
				ref={ref}
				testId={getTestId(MENU_ID_CUSTOMER_SERVICE)}
			>
				{formattedMessage}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				{hasLoadedContent && <CustomerExperiencesExpandableMenu queryReference={queryReference} />}
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
};
